import { useEffect, useState } from 'react';
import { useVgpsRuntime } from 'livekit_web/react';
import type { VgpsLiveSession } from 'livekit_web';
import { leaseLiveSession } from '../session_registry';
import { useDocumentVisible } from './use_document_visible';

export interface UseLiveSessionParams {
  deviceSerial: string;
  channelId: string;
  qualityHint?: 'uhd' | 'hd' | 'sd' | 'auto';
  /**
   * Increment to force the hook to release the current lease and acquire a
   * fresh one (e.g. user clicked "reclaim" after their session was evicted
   * by another tab). Each new value triggers exactly one re-lease cycle.
   */
  reopenKey?: number;
  /**
   * Release the lease (close the relay WebSocket + stop the keepalive) while
   * the tab is hidden, and re-acquire it when the tab becomes visible again.
   * Avoids streaming into a backgrounded tab the browser throttles. Default
   * true. The camera keeps streaming to any other viewer (stream_socket is
   * multi-viewer); only this client disconnects.
   *
   * Note this destroys the AudioSink too, so the page-global AudioHub's
   * remembered cushion depth — not the sink's own state — is what carries the
   * learned network depth across the pause. See `CameraTile.pauseWhenHidden`.
   */
  pauseWhenHidden?: boolean;
}

export interface UseLiveSessionResult {
  session: VgpsLiveSession | null;
  error: Error | null;
  ready: boolean;
}

export function useLiveSession({
  deviceSerial,
  channelId,
  qualityHint,
  reopenKey,
  pauseWhenHidden = true,
}: UseLiveSessionParams): UseLiveSessionResult {
  const runtime = useVgpsRuntime();
  const visible = useDocumentVisible();
  const [session, setSession] = useState<VgpsLiveSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // While hidden (and opted in), hold no lease: the cleanup of the previous
    // run already released it (closing the relay WS + keepalive); this run
    // early-returns without re-acquiring. The next visible transition re-runs
    // the effect and acquires a fresh lease.
    if (!runtime || !deviceSerial || !channelId || (pauseWhenHidden && !visible)) {
      setSession(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let releaseRef: (() => Promise<void>) | null = null;

    const releaseForPageExit = (): void => {
      cancelled = true;
      // pagehide is best effort: dispatching session_stop synchronously starts
      // cleanup while the browser still permits WebSocket writes. Firmware TTL
      // remains the fallback if the browser terminates the page immediately.
      if (releaseRef) void releaseRef();
    };

    setSession(null);
    setError(null);

    console.log(`[useLiveSession] acquiring lease dev=${deviceSerial} ch=${channelId} quality=${qualityHint}`);

    void (async () => {
      try {
        // Ensure signaling is up before opening a session: runtime.openLiveSession
        // assertConnected()s and throws if the WS is down. On the visible
        // transition the provider may also be reconnecting — connectSignaling is
        // reentrant (joins the in-flight handshake), so this just awaits it; on a
        // normal mount where signaling is already connected it is a no-op.
        await runtime.connectSignaling();
        if (cancelled) return;

        const lease = await leaseLiveSession(runtime, { deviceSerial, channelId, qualityHint });
        releaseRef = lease.release;

        if (cancelled) {
          console.log(`[useLiveSession] cancelled after lease acquired, releasing dev=${deviceSerial} ch=${channelId}`);
          await lease.release();
          return;
        }

        console.log(`[useLiveSession] lease acquired sessionId=${lease.session.sessionId} dev=${deviceSerial} ch=${channelId}`);
        setSession(lease.session);
      } catch (err) {
        if (!cancelled) {
          console.error(`[useLiveSession] lease failed dev=${deviceSerial} ch=${channelId}`, err);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();

    window.addEventListener('pagehide', releaseForPageExit);

    return () => {
      window.removeEventListener('pagehide', releaseForPageExit);
      console.log(`[useLiveSession] releasing lease dev=${deviceSerial} ch=${channelId}`);
      releaseForPageExit();
      setSession(null);
    };
  }, [runtime, deviceSerial, channelId, qualityHint, reopenKey, pauseWhenHidden, visible]);

  return { session, error, ready: session !== null };
}
